import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadCloud, FileSpreadsheet, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Import() {
  const navigate = useNavigate();
  const { reload, selectProperty } = useProperty();
  const [file, setFile] = useState(null);
  const [nombre, setNombre] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) return toast.error("El archivo debe ser .xlsx o .xlsm");
    setFile(f);
    setResult(null);
    if (!nombre) setNombre(f.name.replace(/\.(xlsx|xlsm)$/i, ""));
  };

  const importar = async () => {
    if (!file) return toast.error("Selecciona un archivo Excel");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nombre_piso", nombre);
      const { data } = await api.post("/import/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      await reload();
      toast.success("Importación completada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar el Excel");
    } finally {
      setLoading(false);
    }
  };

  const irAlPiso = () => {
    if (result?.property_id) selectProperty(result.property_id);
    navigate("/dashboard");
  };

  return (
    <div data-testid="import-page" className="max-w-2xl">
      <PageHeader title="Importar Excel" subtitle="Alta automática de pisos" />

      {!result ? (
        <div className="space-y-5">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
            className="data-card border-dashed border-2 p-10 text-center cursor-pointer hover:border-primary"
            data-testid="dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="file-input"
            />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="h-10 w-10 text-success" />
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">Pulsa "Importar" para procesarlo</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadCloud className="h-10 w-10 text-primary" />
                <p className="font-medium">Arrastra tu archivo .xlsm aquí</p>
                <p className="text-xs text-muted-foreground">o haz clic para seleccionarlo</p>
              </div>
            )}
          </div>

          <div>
            <Label>Nombre del piso</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Piso Indautxu" data-testid="import-nombre-input" />
          </div>

          <div className="data-card p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">¿Qué se importa?</p>
            Inquilinos, contratos, pagos (con su estado), gastos de suministros y de inversión, y la plantilla de contrato.
            Se usa el mismo formato de tu Excel (hojas <strong>RESUMEN</strong>, <strong>_PAGOS</strong>, <strong>GASTOS</strong>, <strong>CONTRATO</strong>).
          </div>

          <Button onClick={importar} disabled={loading || !file} className="w-full" data-testid="import-button">
            {loading ? "Importando…" : "Importar"}
          </Button>
        </div>
      ) : (
        <div className="data-card p-8 text-center" data-testid="import-result">
          <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-3" />
          <h3 className="font-display text-xl font-medium">¡Importación completada!</h3>
          <p className="text-muted-foreground mt-1">Se ha creado <strong>{result.nombre}</strong></p>
          <div className="grid grid-cols-4 gap-3 mt-6">
            {[["Inquilinos", result.inquilinos], ["Contratos", result.contratos], ["Pagos", result.pagos], ["Gastos", result.gastos]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-border p-3">
                <p className="font-mono text-2xl font-semibold">{v}</p>
                <p className="text-xs text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-center mt-6">
            <Button variant="outline" onClick={() => { setResult(null); setFile(null); setNombre(""); }}>Importar otro</Button>
            <Button onClick={irAlPiso} data-testid="go-to-property-button">Ver el piso <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
